import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';

import { Observable, Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { isEmbeddedInIframe } from '../shared/utils/app-access.util';

@Component({
    imports: [
        CommonModule,
        FormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatCardModule,
        MatButtonModule,
        MatProgressSpinnerModule,
    ],
    selector: 'app-login',
    template: `
        <div class="login-container">
            <mat-card class="login-card">
                <div class="my-2 mb-4">
                    <img class="logo" src="assets/images/logo_cargosprint.svg" alt="CargoSprint logo" />
                </div>
                <h6>Welcome</h6>
                @if (loginNotice$ | async; as loginNotice) {
                    @if (loginNotice) {
                        <p class="notice">{{ loginNotice }}</p>
                    }
                }

                @if (showPasswordLogin$ | async) {
                    <form (ngSubmit)="onLogin()" autocomplete="off">
                        <mat-form-field class="w-full">
                            <mat-label>Email</mat-label>
                            <input
                                id="login-email"
                                matInput
                                [(ngModel)]="email"
                                name="email"
                                autocomplete="email"
                                required
                            />
                        </mat-form-field>
                        <mat-form-field class="w-full">
                            <mat-label>Password</mat-label>
                            <input
                                id="login-password"
                                matInput
                                type="password"
                                [(ngModel)]="password"
                                name="password"
                                autocomplete="current-password"
                                required
                            />
                        </mat-form-field>
                        <div class="text-end mb-2">
                            <mat-checkbox id="login-remember-me" [(ngModel)]="rememberMe" name="rememberMe">
                                Remember Me
                            </mat-checkbox>
                        </div>
                        <button id="login-submit" mat-raised-button color="primary" class="w-full" type="submit">
                            Login
                        </button>
                        @if (pendingMicrosoftLinkRequired$ | async) {
                            <p class="link-hint">
                                We will redirect you to Microsoft to complete secure account linking.
                            </p>
                        }
                    </form>
                } @else {
                    <button
                        id="microsoft-login"
                        mat-stroked-button
                        class="w-full microsoft-button"
                        type="button"
                        (click)="onMicrosoftLogin()"
                        [disabled]="microsoftLoginLoading$ | async"
                    >
                        <span class="microsoft-button-content">
                            @if (!(microsoftLoginLoading$ | async)) {
                                <img
                                    class="microsoft-icon"
                                    src="https://cdn-icons-png.flaticon.com/512/179/179331.png"
                                    alt="Microsoft"
                                />
                            }
                            @if (microsoftLoginLoading$ | async) {
                                <mat-spinner diameter="18"></mat-spinner>
                            }
                            <span class="microsoft-button-label">{{
                                (microsoftLoginLoading$ | async)
                                    ? 'Redirecting to Microsoft...'
                                    : 'Log in using Microsoft'
                            }}</span>
                        </span>
                    </button>
                }
            </mat-card>
        </div>
    `,
    styles: [
        `
            .login-container {
                display: flex;
                height: 80vh;
                align-items: center;
                justify-content: center;
                padding-inline: 16px;
                box-sizing: border-box;
            }

            .login-card {
                width: 300px;
                padding: 20px;
                text-align: center;
                background-color: var(--mat-sys-surface, #fff);
                box-sizing: border-box;

                .logo {
                    width: 200px;
                }
            }

            .w-full {
                width: 100%;
                margin-bottom: 16px;
            }

            .notice {
                margin: 0 0 16px;
                padding: 12px;
                border-radius: var(--cargo-radius-sm2, 8px);
                background: var(--cargo-info-bg, #edf0ff);
                color: var(--cargo-info-text, #005ac2);
                text-align: left;
                font-size: 0.92rem;
            }

            .microsoft-button {
                white-space: nowrap;
                line-height: 1;
                min-height: 40px;
            }

            .microsoft-button-content {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                line-height: 1;
                width: 100%;
            }

            .microsoft-button-content mat-spinner {
                flex: 0 0 auto;
            }

            .microsoft-icon {
                width: 16px;
                height: 16px;
                flex: 0 0 auto;
            }

            .microsoft-button-label {
                display: inline-flex;
                align-items: center;
                line-height: 1;
            }

            .link-hint {
                margin: 0;
                text-align: left;
                color: var(--mat-sys-on-surface-variant, #5d6f82);
                font-size: 0.82rem;
            }
        `,
    ],
})
export class LoginComponent implements OnInit, OnDestroy {
    private static readonly AUTH_DEBUG_LOGS_KEY = 'auth-debug-logs';
    private sessionCheckSub?: Subscription;
    public email = '';
    public password = '';
    public rememberMe = false;

    public readonly showPasswordLogin$: Observable<boolean>;
    public readonly microsoftLoginLoading$: Observable<boolean>;
    public readonly loginNotice$: Observable<string>;
    public readonly pendingMicrosoftLinkRequired$: Observable<boolean>;

    constructor(
        private readonly authService: AuthService,
        private readonly router: Router
    ) {
        this.showPasswordLogin$ = this.authService.showPasswordLogin$();
        this.microsoftLoginLoading$ = this.authService.microsoftLoginLoading$();
        this.loginNotice$ = this.authService.loginNotice$();
        this.pendingMicrosoftLinkRequired$ = this.authService.pendingMicrosoftLinkRequired$();
    }

    public ngOnInit(): void {
        console.log('[LoginComponent] ngOnInit:start', { isInIframe: isEmbeddedInIframe() });
        this.dumpPersistedAuthLogs();
        this.sessionCheckSub = this.authService
            .hasAuthenticatedFirebaseSession$()
            .pipe(take(1))
            .subscribe((isLoggedIn) => {
                console.log('[LoginComponent] ngOnInit:session check', { isLoggedIn });
                if (isLoggedIn) {
                    console.log('[LoginComponent] ngOnInit:navigating /dashboard');
                    this.router.navigate(['/dashboard']);
                    return;
                }
                // When embedded in StaffInterface, auto-trigger login so the user
                // doesn't need to click the button — cookies or postMessage will
                // provide the token without any UI interaction.
                // Skip if the user explicitly logged out in this session.
                const wasExplicitlyLoggedOut = sessionStorage.getItem('explicit_logout') === 'true';
                sessionStorage.removeItem('explicit_logout');
                if (isEmbeddedInIframe() && !wasExplicitlyLoggedOut) {
                    console.log('[LoginComponent] ngOnInit:in iframe, auto-triggering login');
                    this.authService.loginWithMicrosoft().pipe(take(1)).subscribe();
                }
            });
    }

    public ngOnDestroy(): void {
        this.sessionCheckSub?.unsubscribe();
    }

    public onLogin(): void {
        console.log('[LoginComponent] onLogin:submit', {
            email: this.email,
            rememberMe: this.rememberMe,
        });
        this.authService.login(this.email, this.password, this.rememberMe).subscribe();
    }

    public onMicrosoftLogin(): void {
        console.log('[LoginComponent] onMicrosoftLogin:click');
        this.authService.loginWithMicrosoft().pipe(take(1)).subscribe();
    }

    private dumpPersistedAuthLogs(): void {
        try {
            const serializedLogs = sessionStorage.getItem(LoginComponent.AUTH_DEBUG_LOGS_KEY);
            if (!serializedLogs) {
                return;
            }

            const logs = JSON.parse(serializedLogs) as string[];
            if (!Array.isArray(logs) || logs.length === 0) {
                return;
            }

            console.log('[LoginComponent] persisted auth logs (latest first)');
            logs.slice()
                .reverse()
                .forEach((entry) => console.log(entry));
        } catch (error: unknown) {
            console.error('[LoginComponent] dumpPersistedAuthLogs:error', error);
        }
    }
}
